import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, Search, RefreshCw, Plus, UserMinus, Mail, Phone, Shield,
  CheckCircle, XCircle, AlertTriangle, UserPlus, Users, Award,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CoachAssignmentPanel() {
  const [coaches, setCoaches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSubtab, setActiveSubtab] = useState("coaches");
  const [assignDialog, setAssignDialog] = useState({ open: false, coach: null });
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [coachesRes, teamsRes, appsRes] = await Promise.all([
        axios.get(`${API}/admin/coaches`, { withCredentials: true }),
        axios.get(`${API}/academy/teams`, { withCredentials: true }),
        axios.get(`${API}/admin/coach-applications`, { withCredentials: true }),
      ]);
      setCoaches(coachesRes.data || []);
      setTeams(teamsRes.data || []);
      setApplications(appsRes.data || []);
    } catch (error) {
      console.error("Error fetching coach data:", error);
      toast.error("Failed to load coach data");
    } finally {
      setLoading(false);
    }
  };

  const pendingApplications = applications.filter((a) => a.status === "pending");
  const approvedApplications = applications.filter((a) => a.status === "approved");
  const unactivatedApproved = approvedApplications.filter((a) => !a.user_id);

  const handleActivateApproved = async () => {
    setActivating(true);
    try {
      const response = await axios.post(
        `${API}/admin/coaches/activate-approved`,
        {},
        { withCredentials: true }
      );
      if (response.data.activated_count > 0) {
        toast.success(`Activated ${response.data.activated_count} coach account(s)`);
      } else {
        toast.info("All approved coaches are already activated");
      }
      fetchData();
    } catch (error) {
      console.error("Error activating:", error);
      toast.error(error.response?.data?.detail || "Failed to activate coaches");
    } finally {
      setActivating(false);
    }
  };

  const handleApprove = async (applicationId) => {
    try {
      await axios.post(`${API}/admin/coach-applications/${applicationId}/approve`, {}, { withCredentials: true });
      toast.success("Coach application approved");
      fetchData();
    } catch (error) {
      console.error("Error approving:", error);
      toast.error(error.response?.data?.detail || "Failed to approve application");
    }
  };

  const handleReject = async (applicationId) => {
    if (!confirm("Reject this application?")) return;
    try {
      await axios.post(`${API}/admin/coach-applications/${applicationId}/reject`, {}, { withCredentials: true });
      toast.success("Application rejected");
      fetchData();
    } catch (error) {
      console.error("Error rejecting:", error);
      toast.error("Failed to reject application");
    }
  };

  const handleAssignTeam = async () => {
    if (!selectedTeamId || !assignDialog.coach) return;
    setAssigning(true);
    try {
      await axios.post(
        `${API}/admin/coaches/${assignDialog.coach.id}/assign-team?team_id=${selectedTeamId}`,
        {},
        { withCredentials: true }
      );
      toast.success("Coach assigned to team");
      setAssignDialog({ open: false, coach: null });
      setSelectedTeamId("");
      fetchData();
    } catch (error) {
      console.error("Error assigning team:", error);
      toast.error(error.response?.data?.detail || "Failed to assign team");
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignTeam = async (coachId, teamId, teamName) => {
    if (!confirm(`Remove coach from ${teamName}?`)) return;
    try {
      await axios.post(
        `${API}/admin/coaches/${coachId}/unassign-team?team_id=${teamId}`,
        {},
        { withCredentials: true }
      );
      toast.success("Coach removed from team");
      fetchData();
    } catch (error) {
      console.error("Error unassigning:", error);
      toast.error("Failed to remove from team");
    }
  };

  const getAvailableTeams = (coach) => {
    const assignedIds = coach?.academy_coach_teams || [];
    return teams.filter((t) => !assignedIds.includes(t.id));
  };

  const filteredCoaches = coaches.filter((c) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (c.name || "").toLowerCase().includes(term) || (c.email || "").toLowerCase().includes(term);
  });

  if (loading) {
    return (
      <Card className="p-12 bg-slate-900/50 flex justify-center" data-testid="coach-panel-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="coach-assignment-panel">
      {unactivatedApproved.length > 0 && (
        <Card className="p-4 bg-yellow-500/10 border-yellow-500/30">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-200">
                <strong>{unactivatedApproved.length}</strong> approved coach
                {unactivatedApproved.length > 1 ? "es" : ""} need account activation
              </span>
            </div>
            <Button
              onClick={handleActivateApproved}
              disabled={activating}
              size="sm"
              className="bg-yellow-600 hover:bg-yellow-700"
              data-testid="activate-approved-coaches-btn"
            >
              {activating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Activate All
            </Button>
          </div>
        </Card>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          <Button
            variant={activeSubtab === "coaches" ? "default" : "outline"}
            onClick={() => setActiveSubtab("coaches")}
            size="sm"
            className={activeSubtab === "coaches" ? "bg-orange-600 hover:bg-orange-700" : "border-slate-600"}
            data-testid="coach-subtab-active"
          >
            <Users className="w-4 h-4 mr-2" />
            Active Coaches ({coaches.length})
          </Button>
          <Button
            variant={activeSubtab === "applications" ? "default" : "outline"}
            onClick={() => setActiveSubtab("applications")}
            size="sm"
            className={activeSubtab === "applications" ? "bg-yellow-600 hover:bg-yellow-700" : "border-slate-600"}
            data-testid="coach-subtab-applications"
          >
            <Award className="w-4 h-4 mr-2" />
            Applications ({pendingApplications.length} pending)
          </Button>
        </div>
        <Button
          onClick={fetchData}
          variant="outline"
          size="sm"
          className="border-white/20 text-white hover:bg-slate-900/50"
          data-testid="coach-refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {activeSubtab === "coaches" && (
        <>
          <Card className="p-4 bg-slate-900/50">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search coaches..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-600 text-white"
                data-testid="coach-search-input"
              />
            </div>
          </Card>
          <Card className="p-6 bg-slate-900/50">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Coach</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Certifications</TableHead>
                  <TableHead>Assigned Teams</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCoaches.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-400 py-8">
                      No coaches found. Approve coach applications to add coaches.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCoaches.map((coach) => (
                    <TableRow key={coach.id} className="hover:bg-slate-800/30">
                      <TableCell>
                        <p className="font-medium text-white">{coach.name}</p>
                        {coach.nroc_registered && <Badge className="bg-blue-500/20 text-blue-400 text-xs">NROC</Badge>}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-300 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {coach.email}
                        </p>
                        {coach.phone && (
                          <p className="text-xs text-slate-400 flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {coach.phone}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(coach.coaching_certifications || []).map((cert, i) => (
                            <Badge key={i} variant="outline" className="text-xs border-slate-600 text-slate-300">{cert}</Badge>
                          ))}
                          {(!coach.coaching_certifications || coach.coaching_certifications.length === 0) && (
                            <span className="text-slate-500 text-sm">None</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {(coach.teams || []).length === 0 ? (
                            <span className="text-slate-500 text-sm">Not assigned</span>
                          ) : (
                            coach.teams.map((team) => (
                              <div key={team.id} className="flex items-center gap-2">
                                <Badge className="bg-emerald-500/20 text-emerald-400">{team.name}</Badge>
                                <button
                                  onClick={() => handleUnassignTeam(coach.id, team.id, team.name)}
                                  className="text-red-400 hover:text-red-300 p-1"
                                  title="Remove from team"
                                  data-testid={`unassign-coach-${coach.id}-team-${team.id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setAssignDialog({ open: true, coach })}
                          className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/20"
                          disabled={getAvailableTeams(coach).length === 0}
                          data-testid={`assign-coach-${coach.id}-btn`}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Assign Team
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      {activeSubtab === "applications" && (
        <div className="space-y-4">
          <Card className="p-6 bg-slate-900/50">
            <h3 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Pending Applications ({pendingApplications.length})
            </h3>
            {pendingApplications.length === 0 ? (
              <p className="text-slate-400">No pending applications</p>
            ) : (
              <div className="space-y-3">
                {pendingApplications.map((app) => (
                  <Card key={app.id} className="p-4 bg-slate-800/50 border-yellow-500/30">
                    <div className="flex justify-between items-start flex-wrap gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h4 className="text-lg font-semibold text-white">{app.full_name}</h4>
                          {app.is_section_member && <Badge className="bg-emerald-500/20 text-emerald-400">Section Member</Badge>}
                          {app.nroc_registered && <Badge className="bg-blue-500/20 text-blue-400">NROC</Badge>}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-400">
                          <p className="flex items-center gap-1"><Mail className="w-4 h-4" /> {app.email}</p>
                          <p className="flex items-center gap-1"><Phone className="w-4 h-4" /> {app.phone}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApprove(app.id)}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`approve-app-${app.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleReject(app.id)}
                          size="sm"
                          variant="outline"
                          className="border-red-600 text-red-400 hover:bg-red-600/20"
                          data-testid={`reject-app-${app.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      <Dialog open={assignDialog.open} onOpenChange={(open) => !open && setAssignDialog({ open: false, coach: null })}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Assign Team to {assignDialog.coach?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Select a team to assign this coach to
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                <SelectValue placeholder="Select a team" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                {getAvailableTeams(assignDialog.coach).map((team) => (
                  <SelectItem key={team.id} value={team.id} className="text-white hover:bg-slate-700">
                    {team.name} {team.age_group && `(${team.age_group})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog({ open: false, coach: null })} className="border-slate-600">
              Cancel
            </Button>
            <Button
              onClick={handleAssignTeam}
              disabled={!selectedTeamId || assigning}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-assign-coach-btn"
            >
              {assigning && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
